import { pairs } from 'd3-array';
import * as React from 'react';
import { generateLine, isHovering2 } from '@react-financial-charts/interactive/lib/components/index';
import { isDefined, getMouseCanvas, GenericChartComponent } from 'react-financial-charts';
import { getFillStyleWithOpacity } from '@/utils/rgbTorgba';
import type { Coordinate } from '@/types/properties';
import type { MoreProps } from '@/types/commontypes';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;

export interface GannFanProps {
  readonly startXY: Coordinate;
  readonly defaultClassName?: string;
  readonly endXY: Coordinate;
  // readonly fillStyle: Array<string>;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontFill: string;
  readonly interactiveCursorClass?: string;
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly selected: boolean;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly tolerance: number;
  readonly colorWithShow: { show: boolean; color: string }[];
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onMouseDown?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly backgroundOpacity?: number;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

type Line = {
  start: Coordinate;
  end: Coordinate;
  text: string;
};

export class GannFan extends React.Component<GannFanProps> {
  public static defaultProps = {
    strokeWidth: 1,
    tolerance: 4,
    selected: false,
  };

  public render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover, onDoubleClick } = this.props;
    return (
      <ChartComponent
        isHover={this.isHover}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onDoubleClick={onDoubleClick}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { strokeStyle, strokeWidth, fontFamily, fontSize, fontFill, backgroundOpacity } = this.props;

    const lines = this.helper(this.props, moreProps);

    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeStyle;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = fontFill;

    lines.forEach(line => {
      const { x1, y1, x2, y2, label, color } = line;

      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      ctx.fillStyle = color ?? 'rgba(0,0,0,0.2)';
      ctx.beginPath();
      ctx.fillText(label.text, label.x, label.y);
    });
    const pairsOfLines = pairs(lines);

    pairsOfLines.forEach(([line1, line2], _idx) => {
      if (line1.color && line2.color) {
        const finalFillStyle = getFillStyleWithOpacity(line1.color, backgroundOpacity);
        ctx.fillStyle = finalFillStyle;
        ctx.beginPath();
        ctx.moveTo(line1.x1, line1.y1);
        ctx.lineTo(line1.x2, line1.y2);
        ctx.lineTo(line2.x2, line2.y2);
        ctx.closePath();
        ctx.fill();
      }
    });
  };
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onClick) {
      this.props.onClick(e, moreProps);
    }
  };
  private readonly handleMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onMouseDown) {
      this.props.onMouseDown(e, moreProps);
    }
  };
  private readonly isHover = (moreProps: MoreProps) => {
    const { tolerance, onHover } = this.props;
    const { mouseXY } = moreProps;
    const [mouseX, mouseY] = mouseXY;

    let hovering = false;
    if (isDefined(onHover)) {
      const lines = this.helper(this.props, moreProps);

      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < lines.length; i++) {
        const line1 = lines[i];

        const left = Math.min(line1.x1, line1.x2);
        const right = Math.max(line1.x1, line1.x2);
        const top = Math.min(line1.y1, line1.y2);
        const bottom = Math.max(line1.y1, line1.y2);

        const isWithinLineBounds = mouseX >= left && mouseX <= right && mouseY >= top && mouseY <= bottom;
        hovering = isWithinLineBounds && isHovering2([line1.x1, line1.y1], [line1.x2, line1.y2], mouseXY, tolerance);

        if (hovering) {
          break;
        }
      }
    }
    return hovering;
  };

  private readonly getLineCoordinates = (start: Coordinate, endX: number, endY: number, text: string): Line => {
    return {
      start,
      end: [endX, endY],
      text,
    };
  };

  private readonly helper = (props: GannFanProps, moreProps: MoreProps) => {
    const { startXY, endXY, colorWithShow } = props;

    const { xScale, chartConfig } = moreProps;
    const { yScale } = chartConfig;

    if (!isDefined(startXY) || !isDefined(endXY)) {
      return [];
    }
    const [x1, y1] = startXY;
    const [x2, y2] = endXY;

    const dx = x2 - x1;
    const dy = y2 - y1;

    if (dx !== 0 && dy !== 0) {
      const rawLines = [
        this.getLineCoordinates(startXY, x1 + dx / 8, y2, '1/8'),
        this.getLineCoordinates(startXY, x1 + dx / 4, y2, '1/4'),
        this.getLineCoordinates(startXY, x1 + dx / 3, y2, '1/3'),
        this.getLineCoordinates(startXY, x1 + dx / 2, y2, '1/2'),
        { start: startXY, end: endXY, text: '1/1' },
        this.getLineCoordinates(startXY, x2, y1 + dy / 2, '2/1'),
        this.getLineCoordinates(startXY, x2, y1 + dy / 3, '3/1'),
        this.getLineCoordinates(startXY, x2, y1 + dy / 4, '4/1'),
        this.getLineCoordinates(startXY, x2, y1 + dy / 8, '8/1'),
      ];

      const visibleLines = rawLines.map((line, idx) => ({ line, idx })).filter(({ idx }) => colorWithShow?.[idx]?.show);
      const lineCoods = visibleLines.map(({ line, idx }) => {
        const { x1, y1, x2, y2 } = generateLine({
          type: 'RAY',
          start: line.start,
          end: line.end,
          xScale,
          yScale,
        });
        return {
          x1: xScale(x1),
          y1: yScale(y1),
          x2: xScale(x2),
          y2: yScale(y2),
          label: {
            x: xScale(line.end[0]),
            y: yScale(line.end[1]),
            text: line.text,
          },
          color: colorWithShow?.[idx]?.color,
        };
      });

      return lineCoods;
    }
    return [];
  };
}
